import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  CohortState,
  CohortStepStatus,
  CohortStepType,
  ConceptCodesPayload,
  DynamoDBUpdate,
  SearchTerm,
} from 'src/types/cohorts';
import { RootState } from 'src/store';

const stepOrder = [
  CohortStepType.QueryValidation,
  CohortStepType.DataExtraction,
  CohortStepType.CodesSelection,
  CohortStepType.Results,
];

const initialState: CohortState = {
  cohortId: null,
  currentStep: null,
  dataSourceId: null,
  name: null,
  description: null,
  steps: {},
  userId: null,
  userQuery: '',
  rootOrganizationId: null,

  // SSE stream status & errors
  updateStatus: 'idle',
  error: null,

  // Last‐used filter for the stream (undefined until you dispatch startStream)
  streamFilter: undefined,
  allCodesets: [],
  codesContent: {},
  activeTabs: {},
};

// Now create the slice with extraReducers properly defined
export const createCohort = createSlice({
  name: 'createCohort',
  initialState,
  reducers: {
    cohortPageExited: () => {
      return initialState;
    },

    allCodesetsReceived: (state, action: PayloadAction<any[]>) => {
      return {
        ...state,
        allCodesets: action.payload,
      };
    },

    activeTabsUpdated: (
      state,
      action: PayloadAction<{ tabId: number; tabType: 'saved' | 'recommended' }>,
    ) => {
      const { tabId, tabType } = action.payload;
      return {
        ...state,
        activeTabs: {
          ...state.activeTabs,
          [tabId]: tabType,
        },
      };
    },

    cohortStepsUpdated: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        steps: {
          ...state.steps,
          ...action.payload,
        },
      };
    },

    cohortCreated: (state, action: PayloadAction<Partial<CohortState>>) => {
      return { ...state, ...action.payload };
    },

    userQuerySent: (
      state,
      action: PayloadAction<Pick<CohortState, 'userQuery' | 'currentStep'>>,
    ) => {
      return {
        ...state,
        ...action.payload,
        steps: {
          ...state.steps,
          [action.payload.currentStep as CohortStepType]: {
            status: CohortStepStatus.In_Progress,
            data: [],
          },
        },
      };
    },
    dynamoUpdateReceived: (state, action: PayloadAction<DynamoDBUpdate>) => {
      if (!action.payload?.newData) {
        return state;
      }

      return {
        ...state,
        currentStep: action.payload.newData.CurrentStep,
        name: action.payload.newData.Name,
        description: action.payload.newData.Description,
        steps: {
          ...state.steps,
          // ...action.payload.newData.Steps,
          [action.payload.newData.CurrentStep as CohortStepType]:
            action.payload.newData.Steps[action.payload.newData.CurrentStep],
        },
      };
    },

    usedAcceptedStep: (
      state,
      action: PayloadAction<{
        currentStep: CohortStepType;
      }>,
    ) => {
      const { currentStep } = action.payload;

      const currentIndex = stepOrder.findIndex((step) => step === currentStep);

      if (currentIndex === -1) {
        return { ...state };
      }

      if (currentIndex === stepOrder.length - 1) {
        return { ...state };
      }

      const nextStep = stepOrder[currentIndex + 1];
      return {
        ...state,
        currentStep: nextStep,
        steps: {
          ...state.steps,
          [nextStep]: {
            data: [],
            status: CohortStepStatus.In_Progress,
          },
        },
      };
    },

    dataSourceSelected: (
      state,
      action: PayloadAction<{ dataSourceId: string; rootOrganizationId: string | null }>,
    ) => {
      return {
        ...state,
        dataSourceId: action.payload.dataSourceId,
        rootOrganizationId: action.payload.rootOrganizationId,
      };
    },

    toggleCodeEnabled: (state, action: PayloadAction<{ term: string; codeId: string }>) => {
      const { term, codeId } = action.payload;
      const stepData = state.steps[CohortStepType.CodesSelection]
        .data as unknown as ConceptCodesPayload[];
      const terms = stepData[0].payload.content;
      const target = terms.find((t) => t.search_term === term);
      if (!target) return;
      const code = target.matches.recommended.data.find((c) => c.concept_id === codeId);
      if (code) code.enabled = !code.enabled;
    },

    toggleAllCodesEnabled: (
      state,
      action: PayloadAction<{
        term: string;
        codeIds: string[];
        enabled: boolean;
      }>,
    ) => {
      const { term, codeIds, enabled } = action.payload;
      const stepData = state.steps[CohortStepType.CodesSelection]
        .data as unknown as ConceptCodesPayload[];
      const termObj = stepData[0].payload.content.find((t) => t.search_term === term);
      if (!termObj) return;
      termObj.matches.recommended.data.forEach((c) => {
        if (codeIds.includes(c.concept_id)) {
          c.enabled = enabled;
        }
      });
    },
    startCohortStream: (
      state,
      action: PayloadAction<{ pk?: string | null; sk?: string; eventTypes?: string }>,
    ) => {
      state.updateStatus = 'connecting';
      // Optionally stash the filter in state so you can read it in middleware
      state.streamFilter = action.payload;
    },
    stopCohortStream: (state) => {
      state.updateStatus = 'disconnected';
    },

    // 2) Status updates from the middleware
    streamConnected: (state) => {
      state.updateStatus = 'connected';
      state.error = null;
    },
    streamError: (state, action: PayloadAction<string>) => {
      state.updateStatus = 'error';
      state.error = action.payload;
    },
    streamDisconnected: (state) => {
      state.updateStatus = 'disconnected';
    },
  },
});

export const {
  cohortPageExited,
  cohortCreated,
  userQuerySent,
  dynamoUpdateReceived,
  dataSourceSelected,
  usedAcceptedStep,
  toggleAllCodesEnabled,
  toggleCodeEnabled,
  startCohortStream,
  stopCohortStream,
  streamConnected,
  streamError,
  streamDisconnected,
  allCodesetsReceived,
  cohortStepsUpdated,
  activeTabsUpdated,
} = createCohort.actions;

// Selectors
export const selectCohortState = (state: RootState): CohortState => {
  return state.createCohort;
};

export const selectDataSourceId = (state: RootState): string | null => {
  return state.createCohort.dataSourceId;
};

export const selectRootOrganizationId = (state: RootState): string | null => {
  return state.createCohort.rootOrganizationId;
};

export const selectAllCodesets = (state: RootState): any[] => {
  return state.createCohort.allCodesets || [];
};

export const selectSearchTerms = (state: RootState): SearchTerm[] => {
  const stepData = state.createCohort.steps[CohortStepType.CodesSelection];
  if (!stepData) {
    return [];
  }

  const payloadItem = stepData.data[0] as unknown as ConceptCodesPayload | undefined;
  if (!payloadItem) {
    return [];
  }

  return payloadItem.payload.content ?? [];
};

export const selectCohortId = (state: RootState): string | null => {
  return state.createCohort.cohortId;
};

export const createCohortReducer = createCohort.reducer;
